import { BlendPortfolioUtil } from '@ddt-web/blending';
import { IdentifierValidatorsUtil } from '@ddt-web/common';
import { ERROR_CODES } from '@ddt-web/config';
import { AdditionalAnalysisParams, BaseHolding, PortfolioConstants, WweApiParamPositionImpl, WweBenchmarkPosition } from '@ddt-web/domain';
import { ErrorObject } from '@ddt-web/error-handler';
import { HoldingsUtil, SecurityIdentifierUtil } from '@ddt-web/generic-utils';
import { AssetResolveResponse } from '@ddt-web/pas';
import { PortfolioSecurityIdentifierMappingUtil, WwePortfolioPosition, WwePosition, WweSecurity } from '@ddt-web/wealth-web';
import { EntityState } from '@ngrx/entity';
import * as cloneDeep from 'lodash/cloneDeep';
import * as flatten from 'lodash/flatten';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as pick from 'lodash/pick';
import * as uniq from 'lodash/uniq';

export class PasPortfolioPositionsUtil {

	public static mapPositionsToPortfolios(
		ids: string[], // ids from url + child portfolios
		foundSecurities: AssetResolveResponse,
		positionDetails: EntityState<WwePortfolioPosition>,
		associatedPortfolios: EntityState<any>,
		throwSecurityDetailsPartialPortfoliosErrors: boolean,
		additionalAnalysisParams?: AdditionalAnalysisParams
	): Array<{ position: WwePortfolioPosition; missingSecurities: Array<string> }> {
		// Build the WwePortfolioPosition object to store
		const mappedPositions: Array<{ position: WwePortfolioPosition; missingSecurities: Array<string> }> = ids.map(id => {
			const portfolio: WwePortfolioPosition = positionDetails.entities[id];
			const associatedPortfolio: any = associatedPortfolios.entities[id];
			// Different children attributes for portfolios and engagements (due to apb)
			const associatedPortfolioChildren = associatedPortfolio?.children || associatedPortfolio?.positions || [];

			// get parent portfolios - needed when editing portfolio to become a blend (and there are common securities across parent and child)
			const parentPortfolios: WwePortfolioPosition[] = PasPortfolioPositionsUtil.getParentPortfolios(associatedPortfolio, associatedPortfolios, positionDetails);

			const securityKeyIdentifier = this.getSecurityKeyIdentifier(associatedPortfolioChildren, additionalAnalysisParams);

			const positionData = this.getPositionData(
				associatedPortfolioChildren,
				foundSecurities,
				securityKeyIdentifier,
				portfolio,
				parentPortfolios,
				associatedPortfolios,
				throwSecurityDetailsPartialPortfoliosErrors,
				additionalAnalysisParams
			);

			return {
				position: {
					id,
					children: positionData.children
				},
				missingSecurities: positionData.missingSecurities
			};

		});

		return mappedPositions.map((portfolio: { position: WwePortfolioPosition; missingSecurities: Array<string> }) => {
			// In case there are duplicate positions in the portfolio (coming from child portfolios), merge them

			const uniquePositions: WwePosition[] = [];
			portfolio.position.children.forEach((position: WwePosition) => {
				const existingPosition = uniquePositions.find(uniquePosition => position?.requestId === uniquePosition?.requestId);

				if (!!existingPosition) {
					const existingPositionIndex = uniquePositions.indexOf(existingPosition);
					uniquePositions[existingPositionIndex] = { ...existingPosition, weight: existingPosition.weight + position.weight };
				} else {
					uniquePositions.push(position);
				}
				return existingPosition;
			});

			return {
				position: {
					id: portfolio.position.id,
					children: uniquePositions
				},
				missingSecurities: [...Array.from(new Set(portfolio.missingSecurities))]
			};
		});
	}

	/**
	 * Gets all valid securities from the response, updates the weight to be equally divided across them and returns
	 * the portfolio array with one portfolio transient with the provided transientAnalysisId.
	 *
	 * If additionalAnalysisParams.securityResponseFilter is provided, the filtering is applied to the provided response.
	 * If there are no securities after filtering, an error is thrown for the application to handle it.
	 *
	 * @param transientAnalysisId
	 * @param positionDataFromApiCall
	 * @param additionalAnalysisParams
	 * @returns array with transient portfolio
	 */
	public static getPortfoliosToLoadAsTransientAnalysis(
		transientAnalysisId: string,
		positionDataFromApiCall: AssetResolveResponse,
		additionalAnalysisParams?: AdditionalAnalysisParams
	): WwePortfolioPosition[] {
		const wweSecuritiesMap: Map<string, WweSecurity> = positionDataFromApiCall?.assets ?? new Map();

		let securityEntryArray = Array.from(wweSecuritiesMap);
		if (additionalAnalysisParams?.securityResponseFilter) {
			securityEntryArray = securityEntryArray.filter(entry => additionalAnalysisParams.securityResponseFilter(entry[1]));
		}
		const securityKeys = securityEntryArray.map(entry => entry[0]);
		const uniqueWeight = 100 / securityKeys.length;

		const securities = securityKeys.map(key => {
			return { requestId: key, ...wweSecuritiesMap.get(key), weight: uniqueWeight, value: null };
		});

		// if there are no securities due to security filtering, throw error with NO_FUNDS_AFTER_FILTERING - it is up to the application to handle it
		if (additionalAnalysisParams?.securityResponseFilter && isEmpty(securities)) {
			throw new ErrorObject({ errorCodes: ERROR_CODES.WWE_CUSTOM_ERROR_CODES.NO_FUNDS_AFTER_FILTERING });
		}

		return [Object.assign({}, { children: securities, id: transientAnalysisId })];
	}

	public static getTopNWeightedAssetAladdinIds(mappedPortfolios: WwePortfolioPosition[], positionsToReturn: number): Map<string, number> {
		const assetWeightMapping: Map<string, number> = new Map();
		mappedPortfolios
			.map((portfolio: WwePortfolioPosition) => portfolio.children)
			.forEach((children: WwePosition[]) => children
				.forEach((position: WwePosition) => {
					if (assetWeightMapping.has(position.aladdinId)) {
						const currentWeight = assetWeightMapping.get(position.aladdinId);
						assetWeightMapping.set(position.aladdinId, currentWeight + position.weight);
					} else {
						assetWeightMapping.set(position.aladdinId, position.weight);
					}
				}));
		if (assetWeightMapping.size <= positionsToReturn) {
			return assetWeightMapping;
		} else {
			const sortedByWeight: Map<string, number> = new Map(Array.from(assetWeightMapping.entries()).sort((a, b) => b[1] - a[1]));
			return new Map(Array.from(sortedByWeight.entries()).slice(0, positionsToReturn));
		}
	}

	public static mapSortedAladdinIdToAssetName(mappedPortfolios: WwePortfolioPosition[], topWeightedAssets: Map<string, number>): Map<string, string> {
		const uniqueAssetsMapping: Map<string, string> = new Map();
		mappedPortfolios.map((portfolio: WwePortfolioPosition) => portfolio.children)
			.forEach((children: WwePosition[]) => children
				.filter((asset: WwePosition) => topWeightedAssets.has(asset.aladdinId))
				.forEach((asset: WwePosition) => uniqueAssetsMapping.set(asset.aladdinId, asset.description)));

		return new Map(Array.from(uniqueAssetsMapping.entries()).sort((a, b) => {
			const aUpper = a[1].toUpperCase();
			const bUpper = b[1].toUpperCase();
			if (aUpper < bUpper) {
				return -1;
			} else if (aUpper > bUpper) {
				return 1;
			} else {
				return 0;
			}
		}));
	}

	public static buildBenchmarkPosition(benchmarkId: string, benchmarkName: string): WweBenchmarkPosition {
		let benchmarkPosition: WweBenchmarkPosition = null;

		if (IdentifierValidatorsUtil.validateBCusip(benchmarkId)) {
			benchmarkPosition = {
				id: benchmarkName,
				identifiers: {
					aladdinId: benchmarkId
				},
				weight: 100
			};
		}
		return benchmarkPosition;
	}

	public static getSecurityKeyIdentifier(associatedPortfolioChildren: Array<any>, additionalAnalysisParams: AdditionalAnalysisParams): string {
		const shouldUseCommonIdentifier = associatedPortfolioChildren
			.map(holding => holding.applications?.includes('apb') && HoldingsUtil.shouldUseCommonIdentifierForHolding(holding))
			.every(shouldUseCommonIdentifier => shouldUseCommonIdentifier);

		if (shouldUseCommonIdentifier) {
			return 'commonIdentifier';
		} else if (!shouldUseCommonIdentifier && additionalAnalysisParams) {
			return PortfolioSecurityIdentifierMappingUtil.allSecuritiesHaveIdentifier(
				associatedPortfolioChildren,
				additionalAnalysisParams.identifierToBeUsedAsWweApiParamId
			) ? additionalAnalysisParams.identifierToBeUsedAsWweApiParamId : null;
		} else {
			return null;
		}
	}

	public static createWweParamPositions(
		positions: Array<WweBenchmarkPosition | BaseHolding>, prefix: string = '', identifierToBeUsedAsId: string = null
	): WweApiParamPositionImpl[] {
		const shouldUseCustomIdentifier = identifierToBeUsedAsId
			&& uniq(positions.map(position => position[identifierToBeUsedAsId]).filter(identifier => !!identifier))?.length === positions?.length;

		return positions?.map(position => {
			return new WweApiParamPositionImpl(
				shouldUseCustomIdentifier ? position[identifierToBeUsedAsId] : prefix + SecurityIdentifierUtil.buildSecurityAggregateKey(position),
				PortfolioSecurityIdentifierMappingUtil.buildIdentifiersMap(position),
				position['weight'],
				position['value']
			);
		});
	}


	public static createPasParamPositions(
		positions: Array<WweBenchmarkPosition | BaseHolding>, prefix: string = '', identifierToBeUsedAsId: string = null // TO BE UPDATED
	): WweApiParamPositionImpl[] {
		const shouldUseCustomIdentifier = identifierToBeUsedAsId
			&& uniq(positions.map(position => position[identifierToBeUsedAsId]).filter(identifier => !!identifier))?.length === positions?.length;

		// TODO agrigore: add WDS model to PAS model mapping in the request

		return positions?.map(position => {
			return new WweApiParamPositionImpl(
				shouldUseCustomIdentifier ? position[identifierToBeUsedAsId] : prefix + SecurityIdentifierUtil.buildSecurityAggregateKey(position),
				PortfolioSecurityIdentifierMappingUtil.buildIdentifiersMap(position), // TODO create util in pas lib to support model
				position['weight'],
				position['value']
			);
		});
	}


	/** Computes the WWE position data for a list of portfolio children.
	 * This method will call itself recursively to find the flattened position level data for blends and nested blends.
	 *
	 * @param associatedPortfolioChildren the portfolio children - can be flat positions or portfolio positions
	 * @param foundSecurities the position data received from the WWE API call
	 * @param securityKeyIdentifier the identifier which should be used to build the security key
	 * @param portfolio the parent portfolio found in the positions store
	 * @param associatedPortfolios the associated portfolio store state
	 * @param totalWeight the weight of the parent portfolio in the top level portfolio (defaults to 100 for top level positions)
	 */
	private static getPositionData(
		associatedPortfolioChildren: any[],
		foundSecurities: AssetResolveResponse,
		securityKeyIdentifier: string,
		portfolio: WwePortfolioPosition,
		parentPortfolios: WwePortfolioPosition[],
		associatedPortfolios: EntityState<any>,
		throwSecurityDetailsPartialPortfoliosErrors: boolean,
		additionalAnalysisParams: AdditionalAnalysisParams,
		totalWeight: number = 100
	): { children: Array<WwePosition>; missingSecurities: Array<string> } {
		if (isEmpty(associatedPortfolioChildren)) {
			return { children: [], missingSecurities: [] };
		}

		// Compute the positions for the securities in the children
		const missingSecurities = [];

		const directPositions: WwePosition[] = associatedPortfolioChildren
			// remove nested portfolios as they won't be in the securityDetailsResponse as we flatten the portfolio to make the security-details call
			.filter(associatedPosition =>
				!(BlendPortfolioUtil.isChildPortfolio(associatedPosition) || get(associatedPosition, 'positionType') === PortfolioConstants.POSITION_TYPE.INVALID)
			)
			.map(associatedPosition => {
				const associatedPositionKeys = PortfolioSecurityIdentifierMappingUtil.buildSecurityKey(associatedPosition, securityKeyIdentifier);

				const foundInPositionStore: WwePosition = (portfolio?.children ?? [])
					.find(position => associatedPositionKeys === position?.requestId);

				const wweSecuritiesMap: Map<string, WweSecurity> = foundSecurities?.assets ?? new Map();

				const securityFromWweApi = Array.from(wweSecuritiesMap.keys())
					.map((key: string) => {
						if (key === associatedPositionKeys) {
							return { requestId: key, wweSecurity: wweSecuritiesMap.get(key) };
						}
						return null;
					})
					.filter(securityFound => !!securityFound);

				const foundFromWweApi = securityFromWweApi?.[0];

				let foundPositionStoreForParent;
				parentPortfolios.forEach(parentPortfolio => {
					const childFromParent = parentPortfolio.children?.find(position => associatedPositionKeys === position?.requestId);
					if (childFromParent) {
						foundPositionStoreForParent = childFromParent;
					}
				});

				const securityFound = cloneDeep(get(foundFromWweApi, 'wweSecurity') || foundInPositionStore || foundPositionStoreForParent);
				if (!securityFound && throwSecurityDetailsPartialPortfoliosErrors) {
					throw new Error('Security not found.');
				} else if (!securityFound && !throwSecurityDetailsPartialPortfoliosErrors) {
					missingSecurities.push(associatedPosition[securityKeyIdentifier]);
				} else {
					const weightAndValue = pick(associatedPosition, 'weight', 'value');
					// Calculate the weight of the position - multiply the weight of the portfolio position by the weight of the parent portfolio (in percentage)
					weightAndValue.weight = (weightAndValue?.weight ?? 0) * totalWeight / 100;
					return Object.assign(
						securityFound,
						weightAndValue,
						{ requestId: foundInPositionStore?.requestId || foundFromWweApi?.requestId || foundPositionStoreForParent?.requestId }
					);
				}
			})
			.filter(position => !!position);

		// get the associatedPortfolio children data for all nested portfolios to recursively compute the corresponding wwe positions
		const nestedPositions: Array<{ children: Array<WwePosition>; missingSecurities: Array<string> }> = flatten(
			associatedPortfolioChildren
				.filter(associatedPosition => BlendPortfolioUtil.isChildPortfolio(associatedPosition))
				.map((portfolioPosition) => {
					const nestedPortfolio = associatedPortfolios.entities[portfolioPosition.identifier];
					const nestedPortfolioChildren = nestedPortfolio?.children || nestedPortfolio?.positions || [];

					// Calculate the weight of the nested portfolio - multiply the weight of the portfolio position by the weight of the parent portfolio (in percentage)
					// e.g. for a blend containing a portfolio with weight 50% which is 10% made up of a security A then when flattening the positions of the blend, security A's weight will be 5% (10% of 50%)
					const totalNestedPortfolioWeight: number = portfolioPosition.weight * totalWeight / 100;
					return this.getPositionData(
						nestedPortfolioChildren,
						foundSecurities,
						securityKeyIdentifier,
						portfolio,
						parentPortfolios,
						associatedPortfolios,
						throwSecurityDetailsPartialPortfoliosErrors,
						additionalAnalysisParams,
						totalNestedPortfolioWeight
					);
				})
		);

		const nestedChildren = [].concat(...nestedPositions.map(position => position.children));
		const nestedMissingSecurities = [].concat(...nestedPositions.map(position => position.missingSecurities));
		return {
			children: [...directPositions, ...nestedChildren],
			missingSecurities: [...missingSecurities, ...nestedMissingSecurities]
		};
	}


	/**
	 * Returns the parent portfolios for the provided portfolio.
	 * This is required when editing a portfolio and adding a model/ child portfolio with some common securities as the analysed portfolio.
	 * When editing, the parents array is added and needed to identify the common securities, as the security-details API is only made for newly added ones.
	 *
	 * @param associatedPortfolio
	 * @param associatedPortfolios
	 * @param positionDetails
	 * @returns parent portfolios of the provided associatedPortfolio
	 */
	private static getParentPortfolios(
		associatedPortfolio: any, associatedPortfolios: EntityState<any>, positionDetails: EntityState<WwePortfolioPosition>
	): WwePortfolioPosition[] {
		const parentPortfolios: WwePortfolioPosition[] = [];

		associatedPortfolio.parents?.forEach(parentId => {
			// check if parent also has other parent
			if (associatedPortfolios.entities[parentId]) {
				parentPortfolios.push(...PasPortfolioPositionsUtil.getParentPortfolios(associatedPortfolios.entities[parentId], associatedPortfolios, positionDetails));
			}

			// push current parentPortfolio
			parentPortfolios.push(positionDetails.entities[parentId]);
		});

		return parentPortfolios.filter(parentPortfolio => !!parentPortfolio);
	}
}
